import axios from 'axios'
import { isEmpty } from 'lodash-es'
import { computed, inject, type ComputedRef } from 'vue'

import useSectionPopularity from '@canvas-builder/composables/useSectionPopularity'
import { SectionPopularityCategory, SectionTrendlineDirection } from '@lib/types/sectionPopularity'

import { REQUEST_DEFAULT_CANCEL_TIMEOUT } from '@lib/constants/timers.const'
import type { PitcherInfo } from '@lib/types/app'

export enum EntityType {
  FILE = 'File',
  SECTION = 'Section',
  TEMPLATE = 'Template',
}

export function usePopularity() {
  const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
    'pitcherInfo',
    computed(() => undefined),
  )

  async function fetchPopularityData(type: EntityType) {
    const { calculateTrendline, getPopularityCategory, popularityDict } = useSectionPopularity()

    if (!pitcherInfo.value) throw new Error('Pitcher info is not provided')

    try {
      const authorization = `Bearer ${pitcherInfo.value.accessToken}`

      const analyticsRecordsResponse = (
        await axios.get<any>(
          `${window.location.origin}/core/api/protected/analytics?type=popularity&instance_id=${pitcherInfo.value.instanceId}&entity_type=${type}`,
          {
            timeout: REQUEST_DEFAULT_CANCEL_TIMEOUT,
            headers: {
              authorization,
            },
          },
        )
      )?.data

      const analyticsRecords = analyticsRecordsResponse?.data

      if (isEmpty(analyticsRecords)) {
        console.warn(`fetchPopularityData: no data for ${type}`)
        return
      }

      const mostPopularItem: { popularityValue: number; id: string; views: number } = {
        popularityValue: 0,
        id: '',
        views: 0,
      }

      for (const item of analyticsRecords) {
        const {
          percentage_of_views_last_7_days,
          't-1': t1,
          't-2': t2,
          't-3': t3,
          't-4': t4,
          't-5': t5,
          't-6': t6,
          't-7': t7,
          viewed_last_7_days,
          what: id,
        } = item

        const popularityValue = percentage_of_views_last_7_days ? parseInt(percentage_of_views_last_7_days, 10) || 0 : 0
        const views = parseInt(viewed_last_7_days) ?? 0

        if (popularityValue >= mostPopularItem.popularityValue && views > mostPopularItem.views) {
          mostPopularItem.popularityValue = popularityValue
          mostPopularItem.id = id
          mostPopularItem.views = views
        }

        const engagementData = [t1, t2, t3, t4, t5, t6, t7].reverse() as number[]
        const trendline = calculateTrendline(engagementData)

        popularityDict.value[id] = {
          popularity: {
            value: popularityValue,
            category: getPopularityCategory(popularityValue),
          },
          engagement: {
            data: engagementData,
            direction: trendline > 0 ? SectionTrendlineDirection.ASC : SectionTrendlineDirection.DESC,
            trendline,
          },
          views,
        }
      }

      if (popularityDict.value[mostPopularItem.id]?.popularity?.category)
        popularityDict.value[mostPopularItem.id].popularity.category = SectionPopularityCategory.THE_MOST_POPULAR

      return popularityDict.value
    } catch (e) {
      console.error(`fetchPopularityData: failed for ${type}`, e)
    }
  }

  return {
    fetchPopularityData,
  }
}
